import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface Booking {
  id: string
  guestName: string
  guestEmail: string
  guestPhone: string
  unitType: string
  unitNumber: string
  checkIn: string
  checkOut: string
  guests: string
  totalAmount: number
  status: string
  payment: string
  nights?: number
  baseRate?: number
  adminFee?: number
  breakageDeposit?: number
  seasonApplied?: string
}

interface BookingsState {
  bookings: Booking[]
  loading: boolean
  error: string | null
}

const isBrowser = () => typeof window !== 'undefined'

// Save bookings to localStorage
const saveBookingsToStorage = (bookings: Booking[]) => {
  if (!isBrowser()) {
    return
  }

  try {
    localStorage.setItem('resort_bookings', JSON.stringify(bookings))
  } catch (error) {
    console.error('Failed to save bookings to storage:', error)
  }
}

const initialState: BookingsState = {
  bookings: [],
  loading: false,
  error: null,
}

const bookingsSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    addBooking: (state, action: PayloadAction<Booking>) => {
      state.bookings.push(action.payload)
      saveBookingsToStorage(state.bookings)
    },
    updateBooking: (state, action: PayloadAction<Booking>) => {
      const index = state.bookings.findIndex(booking => booking.id === action.payload.id)
      if (index !== -1) {
        state.bookings[index] = action.payload
        saveBookingsToStorage(state.bookings)
      }
    },
    updateBookingStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
      const index = state.bookings.findIndex(booking => booking.id === action.payload.id)
      if (index !== -1) {
        state.bookings[index].status = action.payload.status
        saveBookingsToStorage(state.bookings)
      }
    },
    deleteBooking: (state, action: PayloadAction<string>) => {
      state.bookings = state.bookings.filter(booking => booking.id !== action.payload)
      saveBookingsToStorage(state.bookings)
    },
    updateBookings: (state, action: PayloadAction<Booking[]>) => {
      state.bookings = action.payload
      saveBookingsToStorage(state.bookings)
    }
  },
})

export const { addBooking, updateBooking, updateBookingStatus, deleteBooking, updateBookings } = bookingsSlice.actions
export default bookingsSlice.reducer